import { t } from '../../i18n/i18n';

const MESSAGES = {
  'email-password-required': 'Enter your email and password.',
  INVALID_LOGIN_CREDENTIALS: 'That email and password combination is not recognised.',
  USER_DISABLED: 'This account has been disabled.',
  TOO_MANY_ATTEMPTS_TRY_LATER: 'Too many attempts. Try again in a few minutes.',
  EMAIL_EXISTS: 'An account with this email already exists.',
  WEAK_PASSWORD: 'Choose a stronger password — at least 6 characters.',
};

export const SIGNUP_FAILED_MESSAGE = 'We could not create your account. Please try again.';

export const GOOGLE_FAILED_MESSAGE = 'We could not complete your Google sign-in. Please try again.';

/**
 * The Google endpoint can answer `requires_2fa` too, but its response carries
 * no `temp_token`, so the challenge dialog has nothing to spend — send those
 * users down the email path, which does hand one back.
 */
export const GOOGLE_2FA_MESSAGE =
  'This account is protected by two-factor authentication. Sign in with your email and password instead.';

/**
 * `verify2fa` answered 200 without credentials in it. The code was accepted and
 * there is still no session, so the only honest thing to say is "try again".
 */
export const TWO_FACTOR_FAILED_MESSAGE =
  'We could not complete your sign-in. Please try again.';

/**
 * Wording for the codes this endpoint refuses with. Separate from MESSAGES
 * because the shared map reads INVALID_LOGIN_CREDENTIALS as a sign-in failure,
 * which is the wrong thing to tell someone who is changing their password —
 * here it means the CURRENT password they typed is wrong.
 */
const PASSWORD_CHANGE_MESSAGES = {
  INVALID_LOGIN_CREDENTIALS: 'Your current password is incorrect.',
  WEAK_PASSWORD: 'Choose a stronger password — at least 6 characters.',
  TOO_MANY_ATTEMPTS_TRY_LATER: 'Too many attempts. Try again in a few minutes.',
};

/** Backend error codes are SCREAMING_SNAKE; a message meant for a human is not. */
const isErrorCode = (value) => typeof value === 'string' && /^[A-Z][A-Z0-9_]{2,}$/.test(value);

/**
 * A body's text as something worth showing: a known code becomes its wording,
 * an unrecognised code becomes null so the caller can fall back, and anything
 * already written for a human is passed through.
 */
export const passwordChangeMessage = (value) => {
  if (typeof value !== 'string') return null;
  return t(PASSWORD_CHANGE_MESSAGES[value] ?? (isErrorCode(value) ? null : value));
};

/**
 * `POST /change/password/` answers 200 whatever happens — an unauthenticated
 * call returns the bare string "Please provide Access Token!" with a 200 — so
 * the HTTP status says nothing and the body is the only signal.
 *
 * A refusal arrives as `{ detail: "INVALID_LOGIN_CREDENTIALS" }` with that same
 * 200, so `success !== false` alone is not enough: an error code in the body is
 * itself the failure, and reading it as a success announced the refusal as one
 * and cleared the form. A string body has only ever been an error so far, so it
 * is read as one unless it says otherwise in as many words.
 */
export const passwordChangeOutcome = (data) => {
  if (typeof data === 'string') {
    const ok = /success|updated|changed/i.test(data);
    return { ok, message: passwordChangeMessage(data) };
  }
  if (data && typeof data === 'object') {
    const raw = data.message ?? data.detail ?? data.error;
    const ok = data.success !== false && data.error == null && !isErrorCode(raw);
    return { ok, message: passwordChangeMessage(raw) };
  }
  return { ok: false, message: null };
};

/**
 * Seconds left on a JWT, read from its own `exp` claim.
 *
 * The 2FA endpoint sends no `expiresIn`, and without one the session would be
 * persisted with no expiry at all — `selectIsSessionValid` would then wave a
 * dead token through and let the dashboard flash before the first 401. The
 * token knows when it dies, so ask it.
 *
 * Returns undefined for anything unreadable, which `loginSuccess` already
 * treats as "no expiry known".
 */
export const jwtSecondsRemaining = (token) => {
  try {
    const payload = String(token).split('.')[1];
    // base64url → base64, then pad to a multiple of four for `atob`.
    const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), '=');
    const seconds = Number(JSON.parse(atob(padded)).exp) - Math.floor(Date.now() / 1000);
    return Number.isFinite(seconds) && seconds > 0 ? seconds : undefined;
  } catch {
    return undefined;
  }
};

/**
 * What `POST /get/refreshtoken/` came back with, as the three fields a session
 * is renewed from — or null if there is no token in it, which is what tells the
 * caller to stop and sign the user out.
 *
 * The spelling is read three ways because this backend already answers in three:
 * `/email/signin/` returns Firebase's `idToken` / `expiresIn`, `/auth/2fa/verify/`
 * returns `access_token`, and Firebase's own secure-token service answers
 * `id_token` / `expires_in`. Whichever arrives, the first one present wins.
 */
export const sessionFromRefresh = (data) => {
  const body = data && typeof data === 'object' ? data : {};
  const token = body.idToken ?? body.id_token ?? body.access_token ?? null;
  if (!token) return null;

  const expiresIn = Number(body.expiresIn ?? body.expires_in);
  return {
    token,
    // A rotated refresh token replaces the one in the store; an endpoint that
    // doesn't rotate sends none, and the stored one stays good.
    refreshToken: body.refreshToken ?? body.refresh_token ?? null,
    // The new token's own `exp` when the body doesn't say, for the same reason
    // the 2FA path reads it: a session persisted with no expiry is one
    // `selectIsSessionValid` waves through long after it died.
    expiresIn:
      Number.isFinite(expiresIn) && expiresIn > 0 ? expiresIn : jwtSecondsRemaining(token),
  };
};

/**
 * `/auth/2fa/verify/` answers in the backend's own shape — `access_token`,
 * `refresh_token` and the Django user row — not the Firebase credential shape
 * `/email/signin/` returns. Reshape it into that shape so `loginSuccess` stays
 * the only place that knows what a session is made of.
 *
 * Deliberately field-by-field rather than a spread: the response also carries
 * `totp_secret` and the whole permissions tree, none of which should end up
 * persisted in localStorage.
 */
export const sessionFromTwoFactor = (data) => ({
  idToken: data?.access_token,
  refreshToken: data?.refresh_token ?? null,
  expiresIn: jwtSecondsRemaining(data?.access_token),
  localId: data?.user?.uid ?? null,
  email: data?.user?.email ?? null,
  displayName: [data?.user?.first_name, data?.user?.last_name].filter(Boolean).join(' '),
  // `loginSuccess` reads the staff flags off `user_stat`, which is where the
  // email endpoints put them.
  user_stat: {
    isStaff: Boolean(data?.user?.isStaff ?? data?.user?.is_staff),
    isSuperUser: Boolean(data?.user?.isSuperUser ?? data?.user?.is_superuser),
  },
});

export const authErrorMessage = (error) => {
  const data = error?.data;
  const detail = data?.detail;
  if (detail && MESSAGES[detail]) return t(MESSAGES[detail]);
  if (typeof detail === 'string') return t(detail);
  // The Google endpoint reports its failures under `message`, not `detail`.
  if (typeof data?.message === 'string') return t(data.message);
  if (data && typeof data === 'object') {
    const messages = Object.values(data).find((v) => Array.isArray(v) && typeof v[0] === 'string');
    if (messages) return t(messages[0]);
  }
  if (error?.status === 'FETCH_ERROR') return t('Cannot reach the server. Check your connection.');
  return t('Something went wrong. Please try again.');
};

/**
 * The same refusal, should it ever arrive with a status attached rather than a
 * 200 — the page treats both paths identically.
 */
export const passwordChangeErrorMessage = (error) =>
  passwordChangeMessage(error?.data?.detail) ?? authErrorMessage(error);
